import React from 'react';
import { Transition } from 'react-transition-group';
import Button from '@telia-front/button';
import Container from '@telia-front/container';
import Grid, { GridColumn } from '@telia-front/grid';
import { HeaderLanguages, HeaderEmployee, HeaderActions, } from '@telia-front/header';
import { HeaderContext } from '@telia-front/header-provider';
import { useCurrentBreakPoint } from '@telia-front/helper';
import Image from '@telia-front/image';
import SkipLinks from '@telia-front/skip-links';
import { useTheme } from '@telia-front/theme-provider';
const Header = (props) => {
    const { className, employee, actions, logo, backButton, languages, skipLinks, defaultOpenSection, } = props;
    const { setHeaderHeight } = React.useContext(HeaderContext);
    const [openSection, setOpenSection] = React.useState(defaultOpenSection || null);
    const [openAnimatingSection, setOpenAnimatingSection] = React.useState(defaultOpenSection || null);
    const [overLayVisible, setOverLayVisible] = React.useState(false);
    const dropdownContainer = React.useRef(null);
    const header = React.useRef(null);
    const { breakPoints } = useTheme();
    const currentBreak = useCurrentBreakPoint(breakPoints);
    const isMobile = currentBreak === 'xs' || currentBreak === 'sm';
    const dropdownTransitionTimeout = React.useRef(0);
    const isOpenSectionControlled = (value = props.openSection) => {
        return !!props.onOpenSectionChange && typeof value !== 'undefined';
    };
    const currentOpenSection = isOpenSectionControlled(props.openSection) ? props.openSection : openSection;
    const onOpenSectionChange = (nextOpenSection, prevOpenSection) => {
        if (!isOpenSectionControlled()) {
            setOpenSection(nextOpenSection);
        }
        if (props.onOpenSectionChange) {
            props.onOpenSectionChange(nextOpenSection, prevOpenSection);
        }
    };
    const onActionClick = (section) => {
        const prevOpenSection = currentOpenSection;
        const nextOpenSection = prevOpenSection === section ? null : section;
        setOpenAnimatingSection(nextOpenSection);
        onOpenSectionChange(nextOpenSection, prevOpenSection);
    };
    const closeEverything = () => {
        onOpenSectionChange(null, currentOpenSection);
    };
    const handleSetHeaderHeight = React.useCallback(() => {
        /* istanbul ignore else */
        if (header.current) {
            setHeaderHeight(header.current.clientHeight);
        }
    }, [setHeaderHeight]);
    const BEM = () => {
        const classArray = ['header'];
        if (currentOpenSection) {
            classArray.push('header--open-' + currentOpenSection);
        }
        if (overLayVisible) {
            classArray.push('header--has-overlay');
        }
        if (className) {
            classArray.push(className);
        }
        return classArray.join(' ');
    };
    const getActions = (actions) => {
        /**
         * To get correct current action button.
         * Both sso & nav render same dropdown.
         * sso should be active in desktop and nav should be active in mobile.
         */
        const actionsCurrentSection = (isMobile && currentOpenSection === 'sso' || currentOpenSection === 'nav') ? 'nav' : currentOpenSection;
        const ssoAction = actions.items.find((i) => i.id === 'sso');
        return (React.createElement(HeaderActions, Object.assign({}, actions, { location: "top", onActionClick: onActionClick, currentSection: actionsCurrentSection, currentAnimatingSection: actionsCurrentSection, dropdownContainer: dropdownContainer.current, onDropdownClose: closeEverything, onDropdownTransitionExited: /* istanbul ignore next */ () => setOpenAnimatingSection(null), checkIfDropdownShouldBeVisible: (section) => section === actionsCurrentSection, dropdownOffsetY: isMobile ? 5 : 15, nav: {
                dropdownContent: (React.createElement(React.Fragment, null,
                    isMobile && languages && React.createElement(HeaderLanguages, Object.assign({}, languages)), ssoAction === null || ssoAction === void 0 ? void 0 :
                    ssoAction.dropdownContent)),
                dropdownPadding: 'none',
            } })));
    };
    const getOverlay = () => {
        return (React.createElement(Transition, { in: !!currentOpenSection, timeout: { enter: 10, exit: 100 }, mountOnEnter: true, unmountOnExit: true, onExited: /* istanbul ignore next */ () => setOverLayVisible(false), onEntering: () => setOverLayVisible(true) }, (state) => {
            return (React.createElement("div", { className: 'header__overlay is-' + state, onClick: closeEverything }));
        }));
    };
    React.useEffect(() => {
        const timeout = dropdownTransitionTimeout.current;
        window.addEventListener('resize', handleSetHeaderHeight);
        handleSetHeaderHeight();
        return () => {
            window.removeEventListener('resize', handleSetHeaderHeight);
            window.clearTimeout(timeout);
        };
    }, [handleSetHeaderHeight]);
    return (React.createElement("header", { className: BEM(), ref: header, id: "header" },
        skipLinks && skipLinks.length > 0 && (React.createElement(SkipLinks, { links: skipLinks, setHeaderHeight: handleSetHeaderHeight })),
        employee && (React.createElement(HeaderEmployee, Object.assign({}, employee, { openSection: currentOpenSection, openAnimatingSection: openAnimatingSection, onActionClick: onActionClick, onDropdownClose: closeEverything, dropdownContainer: dropdownContainer.current, onDropdownTransitionExited: /* istanbul ignore next */ () => setOpenAnimatingSection(null) }))),
        React.createElement("div", { className: "header__top" },
            React.createElement(Container, null,
                React.createElement("div", { className: "header__content" },
                    React.createElement(Grid, { align: ['middle-xs', 'end-xs'], noWrap: true },
                        React.createElement(GridColumn, { width: ['xs-2', 'md-4'] }, backButton && (React.createElement(Button, Object.assign({ className: "text-weight-normal", size: "small", type: "text-color", link: true, iconLeft: "#arrow-left" }, backButton)))),
                        logo && (React.createElement(GridColumn, { width: ['xs-8', 'md-4'] },
                            React.createElement(Image, Object.assign({}, logo, { className: "header__logo" })))),
                        React.createElement(GridColumn, { width: ['xs-2', 'md-4'] },
                            React.createElement("div", { className: "header__top-right" },
                                !isMobile && languages && React.createElement(HeaderLanguages, Object.assign({}, languages)),
                                actions && getActions(actions))))))),
        getOverlay(),
        React.createElement("div", { className: "header__dropdown-container", ref: dropdownContainer })));
};
export { Header as default, };
